import { Tag } from "antd";
import { ReactComponent as ViewIcon } from "assets/icon/view-icon.svg";
import {
  Box,
  Button,
  Grid,
  Modal,
  Notification,
  Table,
  Typography,
} from "components";
import { EnumHospitalImportStatus } from "constants/enums/hospital_import_status";
import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";
import { PageContent } from "layouts";
import { BreadcrumbsProps } from "layouts/PageContent";
import { AppColor } from "theme/app-color";
import { forceDownload, gql, homePath, paths } from "utils/helper";
import { HOSPITAL_IMPORT_TABLE_API } from "../HospitalImportTable";
import Authorize from "components/Authorize";
import { PERMISSIONS } from "constants/enums/permissions";

const API = {
  GET_HOSPITAL_IMPORT_CLAIM_REQUESTS: gql`
    query GET_HOSPITAL_IMPORT_CLAIM_REQUESTS($hospitalImportId: String!) {
      hospitalImportClaimRequests(hospitalImportId: $hospitalImportId) {
        hospitalImport {
          id
          name
          status
          fileUrl
          errorLog
        }
        claimRequests {
          id
          referenceId
          employee {
            employeeCode
            firstName
            lastName
            department
          }
          status
          createdAt
          claimName
          claimType
          disease
          receiptNo
          receiptDate
          receiptBook
          hospitalName
          hospitalType
          dayAmount
          amount
        }
      }
    }
  `,
  CANCEL_ALL_REQUEST: gql`
    mutation CANCEL_ALL_REQUEST($hospitalImportId: String!) {
      cancelClaimRequestByHospitalImportId(hospitalImportId: $hospitalImportId)
    }
  `,
};

const enhancer = compose(
  withAuthorize(),
  // withPagination({
  //   defaultLimit: 25,
  //   navigateToPath: ({ queryParams }) => {
  //     paths.requestPath(queryParams).push();
  //   },
  //   rowsPerPageOptions: [25, 50, 100],
  // }),
  withTranslation({
    prefix: "pages.main.hospitalImport.detail.index",
  }),
  withHooks((props: any, hooks: any) => {
    const { t } = props;
    const { useMemo, useParams, useQuery, useCallback, useMutation } = hooks;

    const { id } = useParams();

    const [cancelAllRequest] = useMutation(API.CANCEL_ALL_REQUEST, {
      onCompleted: () => {
        Notification.success(t(".cancelAllRequestSuccess"));
        paths.hospitalImportPath().push();
      },
      refetchQueries: [
        {
          query: HOSPITAL_IMPORT_TABLE_API.HOSPITAL_IMPORT_LIST,
        },
        {
          query: API.GET_HOSPITAL_IMPORT_CLAIM_REQUESTS,
          variables: {
            hospitalImportId: id,
          },
        },
      ],
    });

    const { data, loading, error } = useQuery(
      API.GET_HOSPITAL_IMPORT_CLAIM_REQUESTS,
      {
        variables: {
          hospitalImportId: id,
        },
        fetchPolicy: "network-only",
      }
    );

    const importName = useMemo(
      () => data?.hospitalImportClaimRequests.hospitalImport.name,
      [data?.hospitalImportClaimRequests.hospitalImport.name]
    );

    const breadcrumbs = useMemo(
      (): BreadcrumbsProps[] => [
        { label: t(".home"), path: homePath() },
        { label: t(".hospitalImport"), path: paths.hospitalImportPath() },
        { label: importName, path: null },
      ],
      [t, importName]
    );

    const columns = useMemo(
      () => [
        {
          width: 50,
          field: "actions",
          headerName: " ",
          filterable: false,
          sortable: false,
          type: "actions",
        },
        {
          width: 300,
          field: "referenceId",
          headerName: t(".refId") || "",
        },
        {
          width: 300,
          field: "requesterName",
          headerName: t(".claimRequester") || "",
        },
        // {
        //   width: 150,
        //   field: "department",
        //   headerName: t(".department") || "",
        //   type: "string",
        // },
        {
          width: 250,
          field: "claimName",
          headerName: t(".claimName") || "",
          type: "string",
        },
        {
          width: 250,
          field: "hospitalName",
          headerName: t(".hospitalName") || "",
          type: "string",
        },
        {
          width: 250,
          field: "hospitalType",
          headerName: t(".hospitalType") || "",
          type: "string",
        },
        {
          width: 150,
          field: "disease",
          headerName: t(".disease") || "",
          type: "string",
        },
        {
          width: 150,
          field: "dayAmount",
          headerName: t(".dayAmount") || "",
          type: "string",
        },
        {
          width: 150,
          field: "receiptDate",
          headerName: t(".transferDate") || "",
          type: "string",
        },
        {
          width: 150,
          field: "receiptNo",
          headerName: t(".refNo") || "",
          type: "string",
        },
        {
          width: 150,
          field: "receiptBook",
          headerName: t(".receiptBook") || "",
          type: "string",
        },
        {
          width: 150,
          field: "amount",
          headerName: t(".amount") || "",
          type: "currency",
        },
      ],
      [t]
    );

    const tableData = useMemo(() => {
      if (loading || error) {
        return [];
      }

      const dataItems = data?.hospitalImportClaimRequests.claimRequests.map(
        (req: any) => {
          const { employee } = req;

          const actions = [
            {
              Icon: ViewIcon,
              onClick: () => paths.requestDetailPath(req.id).push(),
            },
          ];

          return {
            ...req,
            requesterName: `[${employee.employeeCode}] ${employee.firstName} ${employee.lastName}`,
            department: employee.department,
            actions,
          };
        }
      );
      return dataItems || [];
    }, [data?.hospitalImportClaimRequests, error, loading]);

    const hospitalImportStatus = useMemo(
      () => data?.hospitalImportClaimRequests.hospitalImport.status,
      [data?.hospitalImportClaimRequests.hospitalImport.status]
    );

    const downloadFile = useCallback(
      async (excelFileUrl: string, fileName: string) => {
        if (fileName && excelFileUrl) {
          const loadFile = async () => {
            try {
              const blob = await fetch(`${excelFileUrl}`).then((r) => r.blob());
              const file = new File([blob], fileName, {
                type:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              if (file) {
                const url = URL.createObjectURL(file);
                forceDownload(url, fileName);
              }
              Notification.success(t(".downloadSuccess"));
            } catch (e) {
              Notification.error(e);
            }
          };

          loadFile();
        } else {
          alert("Coming Soon!");
        }
      },
      [t]
    );

    const handleDownloadFile = useCallback(async () => {
      const fileUrl = data?.hospitalImportClaimRequests.hospitalImport.fileUrl;
      const fileName = data?.hospitalImportClaimRequests.hospitalImport.name;
      if (fileUrl && fileName) {
        downloadFile(fileUrl, fileName);
      }
    }, [
      data?.hospitalImportClaimRequests.hospitalImport.fileUrl,
      data?.hospitalImportClaimRequests.hospitalImport.name,
      downloadFile,
    ]);

    const handleDownloadErrorLogs = useCallback(() => {
      const errorLog =
        data?.hospitalImportClaimRequests.hospitalImport.errorLog;
      if (errorLog) {
        const blob = new Blob([errorLog], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        forceDownload(url, "error_log.txt");

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }
    }, [data?.hospitalImportClaimRequests.hospitalImport.errorLog]);

    const handleDisplayErrorLogs = useCallback(() => {
      const errorLog =
        data?.hospitalImportClaimRequests.hospitalImport.errorLog;
      Modal.alert({
        title: t(".errorLogs"),
        children: <>{errorLog}</>,
        okButtonVariant: "contained",
        okButtonColor: "primary",
        okButtonLabel: t(".close"),
      });
    }, [data?.hospitalImportClaimRequests.hospitalImport.errorLog, t]);

    const handleConfirmCancelAllRequest = useCallback(() => {
      console.log("Cancel all request");
      cancelAllRequest({
        variables: {
          hospitalImportId: id,
        },
      });
    }, [cancelAllRequest, id]);

    const handleCancelAllRequest = useCallback(() => {
      Modal.confirm({
        title: t(".cancelAllRequest"),
        children: (
          <Box display="flex" flexDirection="column">
            <div style={{ marginBottom: "16px" }}>
              <Typography variant="Body/16">
                {t(".cancelAllRequestModal.info1")}
              </Typography>
              <Typography
                variant="Subtitle/16"
                color={AppColor["Primary/Primary"]}
              >
                {` “${importName}” `}
              </Typography>
              <Typography variant="Body/16">
                {t(".cancelAllRequestModal.info2")}
              </Typography>
            </div>
            <Typography variant="Helper/12" color={AppColor["Other/Warning"]}>
              {t(".cancelAllRequestModal.warningInfo")}
            </Typography>
          </Box>
        ),
        cancelButtonLabel: t(".cancelAllRequestModal.close"),
        okButtonLabel: t(".cancelAllRequestModal.confirm"),
        okButtonVariant: "contained",
        // okButtonColor: AppColor["SecondaryText/Danger"],
        cancelButtonVariant: "outlined",
        onOk: async ({ close }: any) => {
          close();
          handleConfirmCancelAllRequest();
        },
      });
    }, [importName, t, handleConfirmCancelAllRequest]);

    return {
      breadcrumbs,
      tableData,
      columns,
      loading,
      importName,
      hospitalImportStatus,
      handleDownloadFile,
      fileUrl: data?.hospitalImportClaimRequests.hospitalImport.fileUrl,
      handleDownloadErrorLogs,
      handleDisplayErrorLogs,
      handleCancelAllRequest,
    };
  })
);

const RequestIndexPage = ({
  totalItems,
  t,
  breadcrumbs,
  columns,
  tableData,
  loading,
  importName,
  hospitalImportStatus,
  handleDownloadFile,
  fileUrl,
  handleDisplayErrorLogs,
  handleDownloadErrorLogs,
  handleCancelAllRequest,
}: any) => (
  <PageContent
    title={importName}
    breadcrumbs={breadcrumbs}
    pageActions={[]}
    loading={loading}
    widthAuto
  >
    <Grid container mb={6} spacing={2}>
      <Grid item xs={12} lg={3}>
        <Typography variant="Header/20" style={{ marginRight: 16 }}>
          {t(".title")}
        </Typography>

        <Tag
          color={
            mappingChipColor[hospitalImportStatus as EnumHospitalImportStatus]
              ?.backgroundColor || "default"
          }
          style={{
            borderRadius: "4px",
            height: "24px",
          }}
        >
          <Typography
            variant="Helper/14"
            color={
              mappingChipColor[hospitalImportStatus as EnumHospitalImportStatus]
                ?.color || "default"
            }
          >
            {t(`client.enum.hospitalImportStatus.${hospitalImportStatus}`)}
          </Typography>
        </Tag>
      </Grid>
      <Grid item xs={12} lg={9}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          {hospitalImportStatus === EnumHospitalImportStatus.ERROR && (
            <>
              <Button
                variant="outlined"
                mr={2}
                onClick={handleDownloadErrorLogs}
              >
                {t(".downloadErrorlogs")}
              </Button>
              <Button
                variant="outlined"
                mr={2}
                onClick={handleDisplayErrorLogs}
              >
                {t(".displayErrorLogs")}
              </Button>
            </>
          )}

          <Button
            variant="outlined"
            mr={2}
            onClick={handleDownloadFile}
            disabled={!fileUrl}
          >
            {t(".download")}
          </Button>
          <Authorize permissions={[PERMISSIONS.HOSPITAL_IMPORT_EDIT]}>
            <Button
              variant="outlined"
              onClick={handleCancelAllRequest}
              disabled={
                tableData.length === 0 ||
                hospitalImportStatus === EnumHospitalImportStatus.CANCELED
              }
            >
              {t(".cancelAllRequest")}
            </Button>
          </Authorize>
        </Box>
      </Grid>
    </Grid>
    <Box
      border="1px solid"
      borderColor={AppColor["Text Light/Secondary"]}
      borderRadius="8px"
      p={4}
    >
      <Table
        columns={columns}
        rows={tableData}
        loading={loading}
        autoHeight
        isShowHover
        rowsPerPageOptions={[25, 50, 100]}
        rowCount={totalItems}
      />
    </Box>
  </PageContent>
);

const mappingChipColor: Record<
  EnumHospitalImportStatus,
  { backgroundColor: string; color: string }
> = {
  [EnumHospitalImportStatus.IMPORTING]: {
    backgroundColor: AppColor["Background/Dark Grey"],
    color: AppColor["Text/Primary"],
  },
  [EnumHospitalImportStatus.PROCESSED]: {
    backgroundColor: AppColor["Other Light/Success"],
    color: AppColor["Other/Success"],
  },
  [EnumHospitalImportStatus.CANCELED]: {
    backgroundColor: AppColor["Other Light/Danger"],
    color: AppColor["Other/Danger"],
  },
  [EnumHospitalImportStatus.ERROR]: {
    backgroundColor: AppColor["Other Light/Danger"],
    color: AppColor["Other/Danger"],
  },
};

export default enhancer(RequestIndexPage);
